import { format } from 'date-fns';
import { InsuranceSubscriberRelationship } from 'domains/client';
import { SUBSCRIBER_RELATIONSHIP_CODE_MAP } from 'domains/signupDirect/businessLogic/relationships';
import {
  EvaluationFormType,
  paymentMethodIsInsurance,
  PrimaryInsuranceSubscriberType,
} from 'domains/signupDirect/schemas/evaluationSchema';
import { checkEligibility, EligibilityCheckPatientParams } from 'domains/signupv4/Insurance/queries';
import { UseFormSetValue } from 'react-hook-form';

type SubmitParams = {
  data: EvaluationFormType;
  setValue: UseFormSetValue<EvaluationFormType>;
};

/** For eligibility ONLY */
const getEligibilityCheckSubscriberDetails = (data: EvaluationFormType): Required<EligibilityCheckPatientParams> => {
  if (data.billingInfo.type === 'privatePay') throw `Cant get insurance details if private pay`;

  const result: EligibilityCheckPatientParams = {
    memberID: data.billingInfo.memberId,
  };

  if (
    data.billingInfo.type === 'insurance-commercial' &&
    data.billingInfo.subscriber.relationShipToClient !== InsuranceSubscriberRelationship.Self
  ) {
    result.dateOfBirth = format(data.billingInfo.subscriber.dateOfBirth, 'yyyy-MM-dd');
    result.firstName = data.billingInfo.subscriber.firstName;
    result.lastName = data.billingInfo.subscriber.lastName;
    result.relationshipToClient = SUBSCRIBER_RELATIONSHIP_CODE_MAP[data.billingInfo.subscriber.relationShipToClient];
  } else {
    result.dateOfBirth = format(data.clientInfo.dateOfBirth, 'yyyy-MM-dd');
    result.firstName = data.clientInfo.firstName;
    result.lastName = data.clientInfo.lastName;
    result.relationshipToClient = SUBSCRIBER_RELATIONSHIP_CODE_MAP[InsuranceSubscriberRelationship.Self];
  }

  return result as Required<EligibilityCheckPatientParams>;
};

/** For Salesforce leads */
export const getInsurancePrimarySubscriberDetails = (data: EvaluationFormType): PrimaryInsuranceSubscriberType => {
  if (data.billingInfo.type === 'privatePay') throw `Cant get insurance details if private pay`;

  if (
    data.billingInfo.type === 'insurance-commercial' &&
    data.billingInfo.subscriber.relationShipToClient !== InsuranceSubscriberRelationship.Self
  ) {
    return data.billingInfo.subscriber as PrimaryInsuranceSubscriberType;
  } else {
    return {
      dateOfBirth: data.clientInfo.dateOfBirth,
      firstName: data.clientInfo.firstName,
      lastName: data.clientInfo.lastName,
      relationShipToClient: InsuranceSubscriberRelationship.Self,
    };
  }
};

export const handleCheckEligibility = async ({ data, setValue }: SubmitParams) => {
  setValue('billingInfo.eligibilityCheckResult', undefined);

  if (!paymentMethodIsInsurance(data.billingInfo)) throw 'Expected billingInfo.type to be insurance';

  const { insuranceData } = data.billingInfo;

  const checkResult = await checkEligibility({
    checkParams: {
      payerID: insuranceData.payerId,
      state: insuranceData.state,
      npi: insuranceData.npiOverride?.toString(),
      payerIDOverride: insuranceData.eligibilityPayerIdOverride?.toString(),
      subscriber: getEligibilityCheckSubscriberDetails(data),
    }, //@TODO(juan) REFACTOR
    insuranceAllowSelfSignUp: insuranceData.allowSelfSignUp,
    insuranceInNetwork: insuranceData.networkStatus,
  });

  setValue('billingInfo.eligibilityCheckResult', checkResult);

  return checkResult;
};
